import { ICheckInItem, ICheckInStep } from "../../models/CheckIn";

// Sentinel prefix used on step IDs that only exist on the frontend. The modal
// uses this to display the canonical question list immediately, even when the
// backend hasn't yet been re-seeded with the new check-in steps documented in
// `docs/pending-endpoints.md` → "GET /checkin/steps seed data". Answers for
// these pending IDs are persisted to `localStorage` so operators can still
// complete the full report; once the backend seeds a matching step with the
// same name the mapping will switch to the real ID transparently.
export const PENDING_STEP_ID_PREFIX = "pending-step:";

export const isPendingStepId = (id: string | null | undefined) =>
  !!id && id.startsWith(PENDING_STEP_ID_PREFIX);

interface CanonicalStep {
  name: string;
  needsValue: boolean;
  options: string | null;
  // Other names the backend might have for this question. The merge logic
  // tries aliases in order and uses the first match so we can re-use existing
  // backend IDs (and their historic answers) even when the display name
  // changes.
  aliases?: string[];
}

// Canonical question set the dashboard expects. Canonical order is the order
// the modal renders them in. Alias names are lower-cased so matching is
// tolerant of the casing the backend seed uses.
export const CANONICAL_CHECK_IN_STEPS: CanonicalStep[] = [
  { name: "Vehicle Images", needsValue: false, options: null },
  {
    name: "VRM & Vin Verified",
    needsValue: false,
    options: null,
    aliases: ["vrm checked", "vin checked", "vrm & vin checked"],
  },
  {
    name: "Keys",
    needsValue: true,
    options: "Yes, No, Spare Only",
    aliases: ["keys present"],
  },
  { name: "Starts and Drives", needsValue: true, options: "Yes, No, N/A" },
  {
    name: "Engine Condition",
    needsValue: true,
    options: "Pass, Faulty, N/A",
    aliases: ["engine"],
  },
  {
    name: "Gearbox Condition",
    needsValue: true,
    options: "Pass, Faulty, N/A",
    aliases: ["gearbox"],
  },
  {
    name: "Exterior Condition",
    needsValue: true,
    options: "Excellent, Good, Poor, Heavy Damage, Parts Missing",
  },
  {
    name: "Interior Condition",
    needsValue: true,
    options: "Excellent, Good, Poor, Heavy Damage, Parts Missing",
  },
  { name: "Hazards", needsValue: true, options: null },
  // Annotated via a bespoke interactive diagram (see `DamageDiagramEditor`).
  // The value is a JSON string containing an array of pin coordinates,
  // severities and optional notes — stored alongside every other check-in
  // answer so the existing persistence path handles it unchanged.
  { name: "Damage Diagram", needsValue: true, options: null },
];

const normalise = (name: string) => name.trim().toLowerCase();

const toPendingId = (canonicalName: string) =>
  `${PENDING_STEP_ID_PREFIX}${normalise(canonicalName).replace(/[^a-z0-9]+/g, "-")}`;

// Build the canonical step list the modal should render. Names in the
// backend's step list are looked up in order of: exact canonical name →
// declared aliases. Whichever matches first becomes the "real" backend step
// for that canonical question so saves hit the existing `/checkin/items`
// endpoint unchanged. Anything not matched is synthesised with a pending ID
// and persisted locally via `localStorage` (see the helpers below).
//
// Important: leftover backend steps that don't map to a canonical question
// are intentionally dropped — the new check-in flow only surfaces the 10
// canonical questions. The backend can still have those older steps active
// for history/migration reasons, they just stop appearing in the modal.
export function mergeCheckInSteps(
  serverSteps: ICheckInStep[]
): { steps: ICheckInStep[]; hasPending: boolean } {
  const byName = new Map<string, ICheckInStep>();
  for (const step of serverSteps) byName.set(normalise(step.name), step);

  let hasPending = false;
  const steps: ICheckInStep[] = CANONICAL_CHECK_IN_STEPS.map((canonical) => {
    const candidateNames = [
      normalise(canonical.name),
      ...(canonical.aliases ?? []).map(normalise),
    ];

    for (const name of candidateNames) {
      const match = byName.get(name);
      if (match) {
        // Re-label the backend step with the canonical name/options so the UI
        // renders the new question text while still using the real row to
        // persist answers — existing answers stored against the old ID keep
        // showing up in the report.
        return {
          ...match,
          name: canonical.name,
          needsValue: canonical.needsValue,
          options: canonical.options,
        };
      }
    }

    hasPending = true;
    return {
      id: toPendingId(canonical.name),
      name: canonical.name,
      needsValue: canonical.needsValue,
      options: canonical.options,
      isActive: true,
      created: "1970-01-01T00:00:00.000Z",
      updated: "1970-01-01T00:00:00.000Z",
    } as ICheckInStep;
  });

  return { steps, hasPending };
}

// -----------------------------------------------------------------------------
// localStorage-backed answers for pending (client-only) steps.
//
// The modal and the row cell both hydrate their state from a merged list of
// server items + local items, so the operator gets a single unified report
// even when some answers are awaiting a backend step seed.
// -----------------------------------------------------------------------------

const LOCAL_STORAGE_KEY = "wilma.checkinItems.v1";

interface LocalCheckInRecord {
  value: string;
  completed: boolean;
  savedAt: string;
}

type LocalCheckInMap = Record<string, Record<string, LocalCheckInRecord>>;

const readStore = (): LocalCheckInMap => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (next: LocalCheckInMap) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore quota / private-mode errors — local persistence is a
    // best-effort convenience, not a correctness requirement.
  }
};

export function loadLocalCheckInItems(vehicleId: string): ICheckInItem[] {
  const store = readStore();
  const perVehicle = store[vehicleId];
  if (!perVehicle) return [];

  return Object.entries(perVehicle).map(([stepId, record]) => ({
    id: `local:${stepId}`,
    created: record.savedAt,
    updated: record.savedAt,
    value: record.value,
    completed: record.completed ? record.savedAt : null,
    completedBy: null,
    checkInStepId: stepId,
    checkInId: "local",
  }));
}

export function saveLocalCheckInItem(
  vehicleId: string,
  stepId: string,
  value: string,
  completed: boolean
) {
  const store = readStore();
  const perVehicle = store[vehicleId] ?? {};
  perVehicle[stepId] = {
    value,
    completed,
    savedAt: new Date().toISOString(),
  };
  store[vehicleId] = perVehicle;
  writeStore(store);
}

// Nukes every locally-persisted answer for a given vehicle. Used by the
// "Reset check-in" flow in the modal so pending (client-only) questions go
// back to unanswered alongside their server-backed counterparts.
export function clearLocalCheckInItems(vehicleId: string) {
  const store = readStore();
  if (!(vehicleId in store)) return;
  delete store[vehicleId];
  writeStore(store);
}
